export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_google_user?: boolean;
  google_id?: string;
  created_at: string;
  updated_at: string;
}

export interface LoginResponse {
  message: string;
  user: User;
}

export interface SignUpRequest {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
}

export interface SignUpResponse {
  message: string;
  user: User;
}

export interface LoginRequest {
  email: string;
  password: string;
}

class AuthService {
  private baseURL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : 'https://stronghireai.com';

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const params = new URLSearchParams({
      email: credentials.email,
      password: credentials.password,
    });

    const response = await fetch(`${this.baseURL}/auth/login?${params}`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    return response.json();
  }

  async signUp(userData: SignUpRequest): Promise<SignUpResponse> {
    const response = await fetch(`${this.baseURL}/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Sign up failed');
    }

    return response.json();
  }

  async logout(): Promise<void> {
    try {
      await fetch(`${this.baseURL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await fetch(`${this.baseURL}/auth/check-auth`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        return data.authenticated ? data.user : null;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  async getProfile(): Promise<User> {
    const response = await fetch(`${this.baseURL}/auth/profile`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch profile data');
    }

    const data = await response.json();
    return data.user;
  }


  googleLogin(): void {
    // Redirect to backend Google OAuth endpoint
    // Backend handles the full OAuth flow and redirects back
    window.location.href = `${this.baseURL}/auth/google-oauth`;
  }

  // Cookie-based auth doesn't need token management
  getToken(): string | null {
    return null;
  }

  setToken(token: string): void {
    // Not needed for cookie-based auth
  }

  clearToken(): void {
    // Not needed for cookie-based auth
  }

  isAuthenticated(): boolean {
    // Will be determined by getCurrentUser()
    return false;
  }

}

const authService = new AuthService();
export default authService;
